import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { 
  Typography, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Tooltip
} from '@mui/material';
import { FormFileUpload } from '../../load-build/formControl/FormFileUpload';
import { uploadDocumentAPI, getDocuments, deleteDocumentAPI, downloadDocumentAPI } from '../api';
import { DOCUMENT_TYPES } from '../utility/constants';
import { log } from '../../Helper';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import SaveAltRoundedIcon from '@mui/icons-material/SaveAltRounded';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';

const Documents = forwardRef((props, ref) => {
  const {
    tabValue,
    setIsLoading,
    getShipToDetailsForMachine
  } = props;

  const [documents, setDocuments] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [isFileSelected, setIsFileSelected] = useState(false);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    handleSave: () => {
      // Implement document save logic here
      toast.success('Documents saved successfully');
    },
    onCloseResetValuesIfNotSaved: () => {
      // Reset any unsaved changes
    },
    validateFormData: () => {
      // Return true if validation passes, false if it fails
      return true;
    }
  }));

  useEffect(() => {
    // Load existing documents when tab is mounted
    if (tabValue?.orderPathId) {
      loadDocuments();
    }
  }, [tabValue?.orderPathId]);

  const loadDocuments = async () => {
    try {
      const response = await getDocuments(tabValue.orderPathId);
      
      if (response.status === 200 && response.data) {
        // Map API response to component format
        const documentsFromAPI = Array.isArray(response.data) ? response.data : 
                                Array.isArray(response.data.documents) ? response.data.documents : [];
        
        // Use documentsFromAPI directly instead of formatting
        setDocuments(documentsFromAPI);
      } else if (response.status === 204) {
        setDocuments([]);
      } else {
        log('❌ Failed to fetch documents:', response);
        toast.error('Failed to load documents');
        setDocuments([]);
      }
    } catch (error) {
      log('❌ Error loading documents:', error);
      toast.error('Error loading documents');
      setDocuments([]);
    }
  };

  const handleUploadClick = () => {
    setUploadDialogOpen(true);
    setSelectedFiles([]);
    setSelectedDocType('');
    setIsFileSelected(false);
  };

  const handleDialogClose = () => {
    setUploadDialogOpen(false);
    setSelectedFiles([]);
    setSelectedDocType('');
    setIsFileSelected(false);
  };

  const handleDrop = (acceptedFiles) => {
    setSelectedFiles(acceptedFiles);
    setIsFileSelected(true);
  };

  const handleConfirmUpload = async () => {
    // Basic validation only
    if (!selectedFiles || selectedFiles.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    if (!selectedDocType) {
      toast.error('Please select a document type');
      return;
    }

    if (!tabValue?.orderPathId) {
      toast.error('Missing order path ID');
      return;
    }

    if (!tabValue?.providerId) {
      toast.error('Missing provider ID');
      return;
    }

    try {
      setIsLoading(true);
      
      // Prepare document details DTO
      const documentDetailsDto = {
        orderPathId: tabValue.orderPathId,
        buildId: tabValue.buildId,
        providerId: tabValue.providerId,
        docType: selectedDocType,
        orderUnit: tabValue.orderUnit,
        orderNumber: tabValue.orderNumber,
        serial17: tabValue.serial17,
        uploadDate: new Date().toISOString(),
        fileCount: selectedFiles.length
      };

      // Call the upload API with all selected files
      const response = await uploadDocumentAPI(documentDetailsDto, selectedFiles);

      if (response.status === 200) {
        toast.success(response.data?.message || `${selectedFiles.length} document(s) uploaded successfully!`);
        
        // Close dialog and reset form
        handleDialogClose();
        
        // Reload documents to show the newly uploaded ones
        loadDocuments();
        
        // Optionally refresh the machine details
        if (getShipToDetailsForMachine) {
          getShipToDetailsForMachine([tabValue.orderUnit, tabValue.orderNumber].join(''));
        }
      } else {
        log('❌ Upload failed with status:', response.status);
        toast.error(response.data?.message || 'Failed to upload documents');
      }
    } catch (error) {
      log('❌ Error uploading documents:', error);
      toast.error('An error occurred while uploading the documents');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDocument = (document) => {
    // Store the document to delete and open confirmation modal
    setDocumentToDelete(document);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!tabValue?.orderPathId) {
        toast.error('Missing order path ID for delete operation');
        return;
      }

      if (!documentToDelete?.documentId) {
        toast.error('Missing document ID for delete operation');
        return;
      }

      setIsLoading(true);

      // Call the delete API - now with additional parameters
      const response = await deleteDocumentAPI(
        tabValue.orderPathId, 
        documentToDelete.documentId,
        tabValue.orderUnit,
        tabValue.orderNumber,
        documentToDelete.documentType
      );
      
      if (response.status === 200) {
        // Handle both JSON response and plain text response
        const message = response.data?.message || response.data || 'Document deleted successfully';
        toast.success(message);
        
        // Close modal first
        setDeleteConfirmOpen(false);
        setDocumentToDelete(null);
        
        // Reload documents from server to get updated list
        await loadDocuments();
        
        // Force refresh of complete machine details to update logs
        if (getShipToDetailsForMachine) {
          await getShipToDetailsForMachine([tabValue.orderUnit, tabValue.orderNumber].join(''));
        }
        
      } else {
        log('❌ Failed to delete document:', response);
        const errorMessage = response.data?.message || response.data || 'Failed to delete document';
        toast.error(errorMessage);
        // Close modal even on error
        setDeleteConfirmOpen(false);
        setDocumentToDelete(null);
      }
      
    } catch (error) {
      log('❌ Error deleting document:', error);
      toast.error('An error occurred while deleting the document');
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    // Close modal and reset document to delete
    setDeleteConfirmOpen(false);
    setDocumentToDelete(null);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      // Prepare input parameters for downloadDocumentAPI
      const name = doc.fileName || doc.name || 'document';
      const providerId = tabValue.providerId;
      const opId = tabValue.orderPathId;
      const docType = doc.documentType;


      // Call the downloadDocumentAPI following getInfoDocument pattern
      const response = await downloadDocumentAPI(name, providerId, opId, docType);

      // Create blob and download link (following getInfoDocument pattern)
      const documentBlob = new Blob([response], { type: "application/pdf" });
      const documentUrl = URL.createObjectURL(documentBlob);
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(documentUrl);

      toast.success('Document downloaded successfully');
    } catch (error) {
      log('❌ Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatDocumentType = (docType) => {
    if (!docType) return 'Unknown';
    
    // Convert underscore-separated values to space-separated display names
    switch (docType) {
      case 'DATE_ADJUSTMENT':
        return 'DATE ADJUSTMENT';
      case 'SHIPPING_ORDER':
        return 'SHIPPING ORDER';
      case 'TRANSPORT_PERMIT':
        return 'TRANSPORT PERMIT';
      case 'COMMUNICATION_INTERNAL':
        return 'COMMUNICATION INTERNAL';
      case 'COMMUNICATION_EXTERNAL':
        return 'COMMUNICATION EXTERNAL';
      default:
        return docType.replace(/_/g, ' '); // Generic replacement for any other underscore cases
    }
  };

  return (
    <div className="p-4">
      {documents && documents.length > 0 ? (
        <>
          {/* Upload Button - Top Right when documents exist */}
          <div className="flex justify-end items-center mb-2">
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={handleUploadClick}
              className="!bg-[#FFDE00] !text-black hover:!bg-[#E6C500] transition-colors"
            >
              Upload Document
            </Button>
          </div>

          {/* Documents List */}
          <div className="space-y-3">
            {/* Available Documents Section */}
            <div className="mt-1">
              <label className="text-sm font-semibold">Available Documents:</label>
              <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                {documents.map((document, index) => (
                  <li key={document.id || index} className="flex items-center justify-between py-2 px-3 border rounded bg-gray-50">
                    <div className="flex items-center space-x-3">
                      <DescriptionIcon className="text-gray-500" fontSize="small" />
                      <div>
                        <div className="text-blue-600 font-medium">
                          {document.fileName || document.name || "Document"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {document.fileType} • {document.fileSize} • Uploaded {formatDateTime(document.uploadedAt)}
                        </div>
                      </div>
                    </div>
                    
                    {/* File Type Display */}
                    <div className="text-sm text-gray-600 font-medium">
                      {formatDocumentType(document.documentType)}
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {/* Show delete icon only for DATE_ADJUSTMENT documents */}
                      {document.documentType === "DATE_ADJUSTMENT" && (
                        <Tooltip title="Delete Document">
                          <DeleteOutlineOutlinedIcon 
                            className="text-black cursor-pointer hover:text-red-600" 
                            onClick={() => handleDeleteDocument(document)}
                          />
                        </Tooltip>
                      )}
                      
                      <Tooltip title="Download">
                        <SaveAltRoundedIcon 
                          className="text-black cursor-pointer hover:text-blue-600" 
                          onClick={() => handleDownloadDocument(document)}
                        />
                      </Tooltip>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      ) : (
        /* Empty State - Centered Layout */
        <div className="flex flex-col items-center justify-start pt-8 min-h-[120px] space-y-4">
          <div className="text-center">
            <DescriptionIcon className="mx-auto text-gray-300 mb-3" style={{ fontSize: 56 }} />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Documents Available</h3>
          </div>
          
          {/* Centered Upload Button */}
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={handleUploadClick}
            size="large"
            className="!bg-[#FFDE00] !text-black hover:!bg-[#E6C500] !py-3 !px-6 !text-base transition-colors"
          >
            Upload Document
          </Button>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle className="flex justify-between items-center">
          <span>Upload Document</span>
          <CloseIcon 
            className="cursor-pointer" 
            onClick={handleDialogClose}
          />
        </DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-2">
            {/* Document Type Select */}
            <div>
              <label className="text-sm font-semibold mb-2 block">
                Document Type <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedDocType}
                onChange={(e) => setSelectedDocType(e.target.value)}
                required
                className="h-10 p-2 text-sm border border-gray-400 rounded w-full box-border focus:outline focus:shadow block"
              >
                <option value="">Select document type</option>
                {DOCUMENT_TYPES.map((docType) => (
                  <option 
                    key={docType.value} 
                    value={docType.value} 
                    disabled={docType.disabled}
                    style={{
                      color: docType.disabled ? '#999' : '#000',
                      backgroundColor: docType.disabled ? '#f5f5f5' : '#fff'
                    }}
                  >
                    {docType.label} {docType.disabled ? '(Coming Soon)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* File Upload */}
            <div>
              <label className="text-sm font-semibold mb-2 block">
                Choose File <span className="text-red-500">*</span>
              </label>
              <FormFileUpload onDrop={handleDrop} fileSelected={isFileSelected} />
              <Typography variant="caption" className="block mt-1 text-gray-500">
                Select any files to upload
              </Typography>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDialogClose} 
            color="inherit"
            className="!text-gray-600 hover:!text-gray-800 hover:!bg-gray-100"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmUpload} 
            variant="contained" 
            className="!bg-[#FFDE00] !text-black hover:!bg-[#E6C500] disabled:!bg-gray-200 disabled:!text-gray-500 transition-colors"
            disabled={!selectedFiles || selectedFiles.length === 0 || !selectedDocType}
          >
            Confirm Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="div">
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this document?
          </Typography>
          {documentToDelete && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              {documentToDelete.fileName || documentToDelete.name || 'Document'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteCancel} 
            color="inherit"
            variant="outlined"
            className="!border-gray-300 !text-gray-600 hover:!border-gray-400 hover:!text-gray-800 hover:!bg-gray-50"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained"
            className="!bg-[#FFDE00] !text-black hover:!bg-[#E6C500] transition-colors"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
});

Documents.displayName = 'Documents';

export default Documents;
